/* ============================================================================
 * Shared helpers for the four Organic Audit report panels.
 *
 * Everything in here is pure utility — no React. The panels import what
 * they need so we don't re-implement HTML escaping, date formatting,
 * download-via-blob, and the "open preview window" trick five times.
 * ========================================================================= */

export const NAVY = "#1B3A4B";
export const GREEN = "#059669";
export const YELLOW_HL = "#FFE680";

export function loadLS(k, fb) {
  try { var d = localStorage.getItem(k); return d ? JSON.parse(d) : fb; }
  catch (e) { return fb; }
}

export function escHtml(s) {
  if (s === undefined || s === null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function fmtDateShort(d) {
  if (!d) return "";
  try { return new Date(d + "T00:00:00").toLocaleDateString("en-US", { year: "numeric", month: "short", day: "2-digit" }); }
  catch (e) { return String(d); }
}

export function fmtRangeShort(from, to) {
  if (!from && !to) return "";
  if (!to) return fmtDateShort(from);
  if (!from) return fmtDateShort(to);
  return fmtDateShort(from) + " — " + fmtDateShort(to);
}

export function fmtMoney(n) {
  var v = Number(n || 0);
  return "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
export function fmtInt(n) {
  return Number(n || 0).toLocaleString("en-US");
}

/* Organic detection. Shipments + invoices store category as either
   "Organic" / "OG" / sometimes "organic" depending on which form
   saved the row. Normalise on one helper so every report uses the
   same definition. */
export function isOrganicCategory(c) {
  if (!c) return false;
  var s = String(c).toLowerCase();
  return s === "organic" || s === "og";
}

/* Commodity classification. Returns "BLK" / "BLU" / "RSP" / "OTHER"
   for organic-relevant products. We prefer the product's productCode
   when we can find the canonical Products record; otherwise we
   pattern-match the displayed name so the report doesn't drop rows
   just because an item points at a stale product id. */
export function commodityFor(productNameOrCode, productsById) {
  /* productsById is an optional { id: productRecord } map. */
  var name = "";
  if (productsById && productsById[productNameOrCode]) {
    var p = productsById[productNameOrCode];
    if (p && p.productCode) return classifyCode(p.productCode) || classifyName(p.productName || p.product);
    name = p && (p.productName || p.product) || "";
  } else {
    name = productNameOrCode || "";
  }
  return classifyName(name);
}
function classifyCode(code) {
  if (!code) return "";
  var u = String(code).toUpperCase();
  if (u.indexOf("BLK") >= 0) return "BLK";
  if (u.indexOf("BLU") >= 0) return "BLU";
  if (u.indexOf("RSP") >= 0 || u.indexOf("RSB") >= 0 || u.indexOf("RAS") >= 0) return "RSP";
  return "";
}
function classifyName(name) {
  if (!name) return "OTHER";
  var n = String(name).toLowerCase();
  if (n.indexOf("blackberry") >= 0 || n.indexOf("mora") >= 0 || n.indexOf("zarzamora") >= 0) return "BLK";
  if (n.indexOf("blueberry")  >= 0 || n.indexOf("arándano") >= 0 || n.indexOf("arandano") >= 0) return "BLU";
  if (n.indexOf("raspberry")  >= 0 || n.indexOf("frambuesa") >= 0) return "RSP";
  return "OTHER";
}
export const COMMODITY_LABELS = {
  en: { BLK: "Blackberry", BLU: "Blueberry", RSP: "Raspberry", OTHER: "Other Organic" },
  es: { BLK: "Mora", BLU: "Arándano", RSP: "Frambuesa", OTHER: "Otros Orgánicos" },
};
export const COMMODITY_ORDER = ["BLK", "BLU", "RSP", "OTHER"];

/* Month bucket helper — returns the first day of the month for
   YYYY-MM-DD. Used to group monthly summaries. */
export function monthKey(dateStr) {
  if (!dateStr) return "";
  return String(dateStr).substring(0, 7); /* YYYY-MM */
}
export function monthLabel(ym, lang) {
  if (!ym) return "";
  var d = new Date(ym + "-01T00:00:00");
  return d.toLocaleDateString(lang === "es" ? "es-MX" : "en-US", { year: "numeric", month: "short" });
}
/* Months between two YYYY-MM-DD dates, inclusive, in chronological order. */
export function monthsBetween(fromStr, toStr) {
  if (!fromStr || !toStr) return [];
  var f = new Date(fromStr + "-01T00:00:00");
  var t = new Date(String(toStr).substring(0, 7) + "-01T00:00:00");
  var out = [];
  while (f <= t) {
    out.push(f.getFullYear() + "-" + String(f.getMonth() + 1).padStart(2, "0"));
    f = new Date(f.getFullYear(), f.getMonth() + 1, 1);
  }
  return out;
}

/* Build a public-URL company logo string — falls back to the local
   svg path so reports render even without internet. CompanyProfile
   stores a base64 logo on the profile record. */
export function getCompanyLogoSrc() {
  try {
    var prof = JSON.parse(localStorage.getItem("gfi-company-profile") || "{}");
    if (prof && prof.logoDataUrl) return prof.logoDataUrl;
  } catch (e) {}
  return ""; /* caller decides what to render when empty */
}

export function getCompanyName() {
  try {
    var prof = JSON.parse(localStorage.getItem("gfi-company-profile") || "{}");
    if (prof && (prof.companyName || prof.fullName)) return prof.companyName || prof.fullName;
  } catch (e) {}
  return "Ground Fresh International, Inc.";
}

/* Open an HTML string in a new tab. Used by Preview buttons. */
export function openPreviewWindow(html) {
  var w = window.open("", "_blank");
  if (!w) { try { window.alert("Pop-up blocked. Allow pop-ups for this site to preview the report."); } catch (e) {} return null; }
  w.document.open();
  w.document.write(html);
  w.document.close();
  return w;
}

/* Trigger a Blob download in the browser. */
export function downloadBlob(blob, filename) {
  var url = URL.createObjectURL(blob);
  var a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(function () { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
}

/* Standard print-template CSS — kept here so every report renders
   with the same look. Inlined by the builders so the preview tab and
   the html2canvas snapshot match exactly. */
export function reportStyles() {
  return ''
    + '@page{size:letter;margin:0.5in 0.55in}'
    + '*{margin:0;padding:0;box-sizing:border-box}'
    + 'body{font-family:Arial,Helvetica,sans-serif;font-size:10.5px;color:#1a1a1a;line-height:1.4;background:#fff}'
    + '.page{max-width:736px;margin:0 auto;padding:6px 40px}'
    + '.header{display:flex;justify-content:space-between;align-items:flex-end;padding-bottom:12px;border-bottom:3px solid ' + NAVY + ';margin-bottom:16px}'
    + '.brand{font-family:"JetBrains Mono",monospace;font-size:22px;font-weight:700;color:' + NAVY + ';letter-spacing:3px}'
    + '.brand-sub{font-size:9px;color:#666;letter-spacing:2px;text-transform:uppercase;margin-top:3px}'
    + '.doc-title{font-size:17px;font-weight:300;color:' + NAVY + ';text-align:right;line-height:1.1;letter-spacing:1px}'
    + '.doc-sub{font-size:10px;color:#666;text-align:right;margin-top:4px;font-family:"Courier New",monospace}'
    + '.section{margin-top:16px}'
    + '.section-title{font-size:11px;text-transform:uppercase;letter-spacing:1.2px;color:' + NAVY + ';font-weight:700;margin-bottom:6px;padding-bottom:3px;border-bottom:2px solid ' + NAVY + '}'
    + '.commodity-title{font-size:13px;font-weight:700;color:' + NAVY + ';margin:14px 0 6px 0;padding-bottom:3px;border-bottom:1.5px solid #d1d5db}'
    + 'table{width:100%;border-collapse:collapse;font-size:10px;margin-bottom:6px}'
    + 'table th{background:#f3f4f6;text-align:left;padding:5px 7px;font-weight:700;font-size:9px;text-transform:uppercase;letter-spacing:0.4px;color:#444;border-bottom:1.5px solid #d1d5db}'
    + 'table td{padding:5px 7px;border-bottom:1px solid #e5e7eb;vertical-align:top}'
    + 'table tr:last-child td{border-bottom:none}'
    + 'table tr.total td{background:#fafafa;font-weight:700;color:' + NAVY + ';border-top:1.5px solid ' + NAVY + ';border-bottom:none}'
    + '.split{display:grid;grid-template-columns:1fr 1fr;gap:14px}'
    + '.hl{background:' + YELLOW_HL + ';padding:1px 3px;border-radius:2px}'
    + '.footer{margin-top:22px;padding-top:8px;border-top:1px solid #d1d5db;font-size:9px;color:#888;text-align:center}'
    + '.balance-row{display:flex;justify-content:space-between;padding:10px 12px;border-radius:6px;border:1.5px solid ' + NAVY + ';background:rgba(27,58,75,0.04);font-weight:700;margin-top:8px}'
    + '.audit-banner{display:inline-block;padding:3px 10px;border:1.5px solid ' + NAVY + ';border-radius:4px;background:rgba(27,58,75,0.06);font-size:11px;font-weight:700;color:' + NAVY + ';letter-spacing:1.2px;text-transform:uppercase;line-height:1.2;font-family:"JetBrains Mono",monospace}'
    + '.audit-period{font-size:9px;color:#666;letter-spacing:0.6px;text-transform:uppercase;margin-top:3px;font-family:"Courier New",monospace}'
    + 'img,svg{max-width:100%;height:auto}'
    + '@media print{body{padding:0}.no-print{display:none}}';
}

/* Standard header block.

   Optional fourth arg `audit` renders the audit name + period
   prominently in the right column above the report title. Format:
     [ ORGANIC AUDIT 2025-2026 ]      ← audit-banner class (boxed)
     Period: Aug 2025 — Jul 2026      ← audit-period class
     <Document Title>                 ← existing doc-title
     <subtitle>                       ← optional
     <dateRange>                      ← optional
   The banner styling lives in reportStyles() so every report picks
   it up automatically. Backward-compatible: callers that don't pass
   `audit` get the original 3-line right column. */
export function reportHeader(title, subtitle, dateRange, audit) {
  var co = getCompanyName();
  var h = '<div class="header">';
  h += '<div><div class="brand">GFI · NEXUS</div><div class="brand-sub">' + escHtml(co) + '</div></div>';
  h += '<div style="text-align:right">';
  if (audit && (audit.name || audit.year)) {
    var auditLabel = audit.name || ("Audit " + audit.year);
    h += '<div class="audit-banner">' + escHtml(auditLabel) + '</div>';
    /* Period — prefer explicit startDate; pair with year+1 when only
       a calendar year is set so the period reads as a season span
       ("2025-2026") instead of just "2025". */
    var periodBits = [];
    if (audit.startDate) periodBits.push(fmtDateShort(audit.startDate));
    if (audit.endDate)   periodBits.push(fmtDateShort(audit.endDate));
    var periodStr = "";
    if (periodBits.length === 2) periodStr = periodBits.join(" — ");
    else if (periodBits.length === 1) periodStr = periodBits[0];
    else if (audit.year) periodStr = String(audit.year) + " – " + String(Number(audit.year) + 1);
    if (periodStr) h += '<div class="audit-period">' + escHtml(periodStr) + '</div>';
  }
  h += '<div class="doc-title" style="margin-top:' + (audit ? 6 : 0) + 'px">' + escHtml(title) + '</div>';
  if (subtitle) h += '<div class="doc-sub">' + escHtml(subtitle) + '</div>';
  if (dateRange) h += '<div class="doc-sub">' + escHtml(dateRange) + '</div>';
  h += '</div></div>';
  return h;
}

/* Standard footer with auditor + audit name. */
export function reportFooter(audit, lang) {
  var ES = lang === "es";
  var nowStr = new Date().toLocaleString();
  return '<div class="footer">'
    + (ES ? "Auditoría" : "Audit") + ': ' + escHtml(audit && audit.name || "—")
    + (audit && audit.auditorName ? '   ·   ' + (ES ? "Auditor" : "Auditor") + ': ' + escHtml(audit.auditorName) : '')
    + '   ·   ' + (ES ? "Generado el" : "Generated") + ': ' + escHtml(nowStr)
    + '   ·   ' + escHtml(getCompanyName())
    + '</div>';
}

/* Policy header — for permanent policy/procedure sheets that are NOT
   tied to a specific audit. Shows brand + company + title + subtitle
   + version, but no audit banner/period. */
export function policyHeader(title, subtitle, version) {
  var co = getCompanyName();
  var h = '<div class="header">';
  h += '<div><div class="brand">GFI · NEXUS</div><div class="brand-sub">' + escHtml(co) + '</div></div>';
  h += '<div style="text-align:right">';
  h += '<div class="doc-title">' + escHtml(title) + '</div>';
  if (subtitle) h += '<div class="doc-sub">' + escHtml(subtitle) + '</div>';
  if (version) h += '<div class="doc-sub">' + escHtml(version) + '</div>';
  h += '</div></div>';
  return h;
}

/* Policy footer — only the company name, no audit/auditor/date. */
export function policyFooter() {
  return '<div class="footer">' + escHtml(getCompanyName()) + '</div>';
}
